import React, { useEffect, useState, useCallback } from 'react';
import { rookiesAPI, playersAPI } from '../../api/api';

const ManageRookies = () => {
  const [rookies, setRookies]       = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [clearing, setClearing]     = useState(false);
  const [error, setError]           = useState(null);

  // ── Load data ──
  const loadData = useCallback(async () => {
    try {
      const [rookiesRes, playersRes] = await Promise.all([
        rookiesAPI.getAll(),
        playersAPI.getAll(true)
      ]);
      setRookies(rookiesRes.data);
      setAllPlayers(playersRes.data);
    } catch (e) {
      setError('Failed to load player data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Toggle single player ──
  const toggle = async (playerId, makeRookie) => {
    try {
      await rookiesAPI.setStatus(playerId, makeRookie);
      // Optimistic update
      if (makeRookie) {
        const player = allPlayers.find(p => p.id === playerId);
        if (player) setRookies(prev => [...prev, { ...player, isRookie: true }].sort((a, b) => a.lastName.localeCompare(b.lastName)));
      } else {
        setRookies(prev => prev.filter(p => p.id !== playerId));
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update player');
    }
  };

  // ── Clear all ──
  const clearAll = async () => {
    if (!window.confirm(`Remove all ${rookies.length} rookies? Use this when switching to a new 2K version.`)) return;
    setClearing(true);
    try {
      await rookiesAPI.clearAll();
      setRookies([]);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to clear rookies');
    } finally {
      setClearing(false);
    }
  };

  // ── Filtered non-rookie list ──
  const rookieIds = new Set(rookies.map(r => r.id));
  const filtered = allPlayers.filter(p => {
    if (rookieIds.has(p.id)) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
    const teamName = (p.team?.name || '').toLowerCase();
    return fullName.includes(q) || teamName.includes(q);
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-500 dark:text-gray-400">Loading players...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">🌟 Manage Rookies</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Rookies are based on real NBA rookie status in the current 2K version.
            Run <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">setRookies2K27.js</code> when you switch versions.
          </p>
        </div>
        <button
          onClick={clearAll}
          disabled={clearing || rookies.length === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 disabled:opacity-40
            text-white font-semibold text-sm transition"
        >
          {clearing ? '⏳ Clearing...' : `🗑️ Clear All Rookies (${rookies.length})`}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-3 underline">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── LEFT: Current Rookies ── */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900 overflow-hidden">
          <div className="px-5 py-3 bg-gradient-to-r from-nba-blue to-nba-red flex items-center justify-between">
            <h2 className="text-white font-bold">Current Rookies</h2>
            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {rookies.length}
            </span>
          </div>

          {rookies.length === 0 ? (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500">
              <div className="text-4xl mb-2">📭</div>
              <p className="text-sm">No rookies set. Search and add them on the right.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[520px] overflow-y-auto">
              {rookies.map(p => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition group">
                  {p.imageUrl && (
                    <img src={p.imageUrl} alt={`${p.firstName} ${p.lastName}`}
                      className="w-8 h-8 rounded-full object-cover bg-gray-200 dark:bg-gray-700 shrink-0"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {p.firstName} {p.lastName}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {p.team?.name || 'Free Agent'} · {p.position} · OVR {p.overallRating}
                    </div>
                  </div>
                  <button
                    onClick={() => toggle(p.id, false)}
                    className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full
                      text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20
                      opacity-0 group-hover:opacity-100 transition text-lg font-bold"
                    title="Remove rookie status"
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── RIGHT: Add Rookies ── */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900 overflow-hidden">
          <div className="px-5 py-3 bg-gray-100 dark:bg-gray-700 flex items-center justify-between">
            <h2 className="font-bold text-gray-700 dark:text-gray-200">Add Rookies</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">{filtered.length} players</span>
          </div>

          {/* Search */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or team..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm
                bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100
                focus:outline-none focus:ring-2 focus:ring-nba-blue"
            />
          </div>

          <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[460px] overflow-y-auto">
            {filtered.length === 0 && (
              <li className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                {search ? 'No players match your search.' : 'All players are already rookies.'}
              </li>
            )}
            {filtered.map(p => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                {p.imageUrl && (
                  <img src={p.imageUrl} alt={`${p.firstName} ${p.lastName}`}
                    className="w-8 h-8 rounded-full object-cover bg-gray-200 dark:bg-gray-700 shrink-0"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                    {p.firstName} {p.lastName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {p.team?.name || 'Free Agent'} · {p.position} · OVR {p.overallRating}
                  </div>
                </div>
                <button
                  onClick={() => toggle(p.id, true)}
                  className="shrink-0 px-3 py-1 rounded-lg text-xs font-semibold
                    border border-nba-blue text-nba-blue hover:bg-nba-blue hover:text-white transition"
                >
                  + Add
                </button>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </div>
  );
};

export default ManageRookies;
